/**
 * The review screen's **Unpin** (#1988) — the cell's Unpin, on a pinned row.
 *
 * On *Fix and re-solve*'s review, a row the lead pinned cannot be rejected:
 * the draft was solved around it and the apply refuses to change a pinned
 * cell. What the lead can reconsider is whether it STAYS pinned. Unpin →
 * an inline confirm → the row reads *Unpins after Apply*: the seat lands as
 * placed, then the pin the apply keeps is released through the fixed-shifts
 * DELETE (`releaseReviewPins`), so the next solve may change it — the same
 * end state as **Unpin** on the cell. *Keep pinned* undoes it before Apply.
 *
 * Offered only where the host says which team and window the pins land in
 * (`ReviewUnpinContext`, provided by `<FixAndResolveHost/>`), and only on a
 * row holding a station pin: an off pin is the absence itself (removed on
 * the leave side), and the knock-on's one pin IS the seat being opened —
 * its undo is Discard.
 */
import { createContext, useState } from "react";

import type { ReviewUnpinScope } from "../../api/leadFixedUnpin";
import { cn } from "../../lib/cn";

/** Which team / window a review's pins are written to; null = no Unpin. */
export const ReviewUnpinContext = createContext<ReviewUnpinScope | null>(null);

export function ReviewPinControl({
  name,
  canUnpin,
  unpinned,
  onUnpin,
  onRestore,
  testid,
}: {
  name: string;
  canUnpin: boolean;
  unpinned: boolean;
  onUnpin: () => void;
  onRestore: () => void;
  testid: string;
}) {
  const [confirming, setConfirming] = useState(false);
  const label = "text-[10px] uppercase tracking-wide text-fg-faint";
  const link = "text-[11px] text-fg-muted underline-offset-2 hover:text-fg hover:underline";
  if (!canUnpin) return <span className={label}>Pinned</span>;
  if (unpinned) {
    return (
      <span className="flex shrink-0 items-center gap-2" data-testid={`${testid}-unpinned`}>
        <span className={label}>Unpins after Apply</span>
        <button
          type="button"
          className={link}
          data-testid={`${testid}-keep-pinned`}
          onClick={onRestore}
        >
          Keep pinned
        </button>
      </span>
    );
  }
  if (confirming) {
    return (
      // Its own line under the name (the row header wraps), so the name
      // stays readable while the lead decides.
      <span
        className="flex basis-full flex-wrap items-center justify-end gap-x-3 gap-y-1"
        data-testid={`${testid}-unpin-confirm`}
      >
        <span className="mr-auto text-[11px] text-fg-muted">
          {name} lands as placed; the next solve may change it.
        </span>
        <span className="flex shrink-0 items-center gap-3">
          <button
            type="button"
            className={cn(link, "font-medium text-danger")}
            data-testid={`${testid}-unpin-yes`}
            onClick={() => {
              setConfirming(false);
              onUnpin();
            }}
          >
            Unpin
          </button>
          <button type="button" className={link} onClick={() => setConfirming(false)}>
            Keep
          </button>
        </span>
      </span>
    );
  }
  return (
    <span className="flex shrink-0 items-center gap-2">
      <span className={label}>Pinned</span>
      <button
        type="button"
        className={link}
        data-testid={`${testid}-unpin`}
        onClick={() => setConfirming(true)}
      >
        Unpin
      </button>
    </span>
  );
}
